import { useState } from 'react';
import api from '../../../services/api';
import type { BoardroomBrandLevel, BoardroomBrandSummary } from '../../../types';
import { BrandTiles, LEVEL_WORDS, SiteWarning, progressLine } from './BrandReport';

/**
 * On a company's page: who is going after its searches, in a few numbers, and
 * the button that starts (or repeats) the searches. The full report is a card
 * of its own; this opens it.
 */
export default function BrandSection({
  boardroomId,
  itemId,
  name,
  summary,
  onStarted,
  onOpenReport,
  onOpenItem,
}: {
  boardroomId: number;
  itemId: number;
  name: string;
  summary: BoardroomBrandSummary | null | undefined;
  /** Reload the page once the searches are queued. */
  onStarted: () => void;
  onOpenReport: () => void;
  onOpenItem: (itemId: number) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = Boolean(summary?.started);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.startBoardroomBrandSearches(boardroomId, itemId);
      onStarted();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the searches');
    } finally {
      setBusy(false);
    }
  };

  const btn =
    'px-2 py-0.5 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50';
  const risk = summary?.readings?.threat ?? 0;
  const open = summary?.readings?.opportunity ?? 0;
  const landing = (summary?.progress?.pending ?? 0) > 0;
  return (
    <section
      className="shrink-0 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-3 space-y-2 text-xs"
      data-testid="profile-brand"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="flex-1 text-[11px] uppercase tracking-wide text-gray-400">Who’s going after {name}’s searches</h3>
        {started && (
          <button type="button" onClick={onOpenReport} className={btn} data-testid="brand-open-report">
            Open full report
          </button>
        )}
        <button type="button" onClick={start} disabled={busy} className={btn} data-testid="brand-start">
          {busy ? 'Starting…' : started ? 'Search again' : `Who’s going after ${name}’s searches?`}
        </button>
      </div>
      {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
      {!started ? (
        <p className="text-gray-500 dark:text-gray-400">
          Searches what people search about {name} - its name, products, pricing, reviews, problems, alternatives and
          “vs” its rivals - and shows who holds page one, what is at risk and where the openings are.
        </p>
      ) : (
        <>
          {/* WHAT IS AT RISK AND WHERE THE OPENINGS ARE — the reason anyone
              opens this — as links straight into the report. */}
          <p className="flex flex-wrap items-center gap-3" data-testid="brand-readings">
            {risk > 0 && (
              <button type="button" onClick={onOpenReport} className="font-medium text-rose-700 dark:text-rose-300 hover:underline" data-testid="brand-at-risk">
                {risk} at risk
              </button>
            )}
            {open > 0 && (
              <button type="button" onClick={onOpenReport} className="font-medium text-emerald-700 dark:text-emerald-300 hover:underline" data-testid="brand-openings">
                {open} opening{open === 1 ? '' : 's'}
              </button>
            )}
            {!risk && !open && (
              <span className="text-gray-500 dark:text-gray-400">
                {landing ? 'Findings appear as the searches come back.' : 'Nothing found yet on these searches.'}
              </span>
            )}
            <span className="text-gray-400">{progressLine(summary?.progress)}</span>
          </p>
          {summary?.levels && (
            <p className="flex flex-wrap gap-1.5" data-testid="brand-levels">
              {(Object.keys(LEVEL_WORDS) as BoardroomBrandLevel[])
                .filter(l => (summary.levels?.[l] ?? 0) > 0)
                .map(l => (
                  <span key={l} className={`rounded px-1.5 py-0.5 text-[11px] ${LEVEL_WORDS[l].tone}`} title={LEVEL_WORDS[l].hint}>
                    {summary.levels![l]} {LEVEL_WORDS[l].label.toLowerCase()}
                  </span>
                ))}
            </p>
          )}
          {summary?.site_warning && <SiteWarning text={summary.site_warning.text} />}
          <BrandTiles s={summary as BoardroomBrandSummary} />
          {(summary?.top_interceptors?.length ?? 0) > 0 && (
            <p className="text-gray-600 dark:text-gray-300">
              Most visible rivals:{' '}
              {summary!.top_interceptors!.map((r, i) => (
                <span key={r.id}>
                  {i > 0 && ', '}
                  <button type="button" onClick={() => onOpenItem(r.id)} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                    {r.name}
                  </button>{' '}
                  ({r.share}%)
                </span>
              ))}
            </p>
          )}
        </>
      )}
    </section>
  );
}
